import { useNavigate } from "react-router-dom";
import { ArrowRight, UploadCloud } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAIWorkspace } from "@/hooks/useAIWorkspace";
import { cn } from "@/lib/utils";
import { CoTile } from "./parts";
import { fitTone } from "./portal";

function StepCard({ n, title, sub, cta, onClick, primary, testId }) {
  return (
    <div className={cn("hs-card p-5 sm:p-6 flex flex-col", primary && "border-primary/40")} data-testid={testId}>
      <div className={cn("hs-mono text-xs font-semibold tracking-[0.14em]", primary ? "text-primary" : "text-muted-foreground")}>STEP {n}</div>
      <h3 className="mt-2 text-[17px] font-semibold">{title}</h3>
      <p className="mt-1 mb-6 text-sm text-muted-foreground">{sub}</p>
      <button onClick={onClick} data-testid={`${testId}-cta`}
        className={cn("mt-auto h-11 rounded-xl text-sm font-semibold transition-[filter,background-color]",
          primary ? "bg-primary text-primary-foreground hover:brightness-110" : "border border-border hover:bg-foreground/[0.04]")}>{cta}</button>
    </div>
  );
}

export function FirstRun({ name, opp, pct }) {
  const nav = useNavigate();
  const open = useAIWorkspace();
  const ranked = (opp?.opportunities || []).filter((o) => o.fit != null);
  const top = ranked[0];
  const matching = ranked.filter((o) => o.fit >= 45).length;
  const target = pct >= 90 ? 100 : 90;
  return (
    <div className="space-y-6 hs-stagger" data-testid="home-first-run">
      <section className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-r from-card via-card to-primary/[0.12] px-6 sm:px-9 py-8 sm:py-10">
        <div className="relative z-10 max-w-xl">
          <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight" data-testid="home-greeting">You're all set{name ? `, ${name}` : ""}</h1>
          <p className="mt-3 text-[15px] text-muted-foreground leading-relaxed">
            {opp === undefined ? "Matching you to open roles…" : `${matching} role${matching === 1 ? "" : "s"} match your profile. Here's how to get the most from HireStream in your first week.`}
          </p>
        </div>
        <img src="/pop3d_candidate.png" alt="" aria-hidden="true" className="hidden md:block absolute right-6 lg:right-16 top-1/2 -translate-y-1/2 h-44 w-44 object-contain drop-shadow-[0_20px_40px_rgba(16,185,129,0.25)] hs-rise" />
      </section>
      <div className="grid md:grid-cols-3 gap-4">
        <StepCard n={1} primary title="Apply to your top match" testId="first-run-step-1"
          sub={top ? `${top.job.title}${top.company_name ? ` at ${top.company_name}` : ""} · ${Math.round(top.fit)}% fit` : "Browse roles matched to your profile"}
          cta={top ? "Review & apply" : "Browse roles"} onClick={() => nav(top ? `/candidate/jobs?job=${top.job.id}` : "/candidate/jobs")} />
        <StepCard n={2} title={pct >= 100 ? "Keep your profile fresh" : `Lift your profile to ${target}%`} testId="first-run-step-2"
          sub={`${pct}% today · a few quick fixes`} cta="Fix with Copilot"
          onClick={() => open("Review my profile and suggest the quickest fixes to make it stronger.", { previewUrl: "/candidate/profile" })} />
        <StepCard n={3} title="Let Auto-apply work for you" testId="first-run-step-3"
          sub="The agent applies to roles above your fit bar, within your cap" cta="Set up rules" onClick={() => nav("/candidate/auto-apply")} />
      </div>
      <TopMatches ranked={ranked} loading={opp === undefined} />
    </div>
  );
}

function TopMatches({ ranked, loading }) {
  const nav = useNavigate();
  return (
    <section aria-labelledby="top-matches-title" data-testid="first-run-matches">
      <header className="flex items-center justify-between mb-3">
        <h2 id="top-matches-title" className="text-[17px] font-semibold">Top matches for you</h2>
        <button onClick={() => nav("/candidate/jobs")} className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="first-run-all-jobs">All {ranked.length} <ArrowRight className="h-3.5 w-3.5" /></button>
      </header>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-[14px]" />) : ranked.slice(0, 4).map((o) => (
          <button key={o.job.id} onClick={() => nav(`/candidate/jobs?job=${o.job.id}`)} data-testid={`top-match-${o.job.id}`}
            className="hs-card p-5 text-left hover:border-foreground/20 hover:-translate-y-0.5 transition-[border-color,transform]">
            <span className="flex items-start justify-between"><CoTile name={o.company_name || o.job.title} size="sm" /><span className={cn("text-lg font-display font-semibold tabular-nums", fitTone(o.fit))}>{Math.round(o.fit)}%</span></span>
            <span className="mt-4 block font-semibold truncate">{o.job.title}</span>
            <span className="block text-[13px] text-muted-foreground truncate">{[o.company_name, o.job.location].filter(Boolean).join(" · ")}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

export function ProfileNotReady({ status }) {
  const nav = useNavigate();
  return (
    <section className="hs-card border-primary/30 p-6 sm:p-8 flex items-start gap-4" data-testid="cv-first-card">
      <span className="h-12 w-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0"><UploadCloud className="h-6 w-6" /></span>
      <div>
        <h2 className="text-xl font-display font-semibold">{status === "draft" ? "Your profile is ready to review" : status === "parsing" ? "We're building your profile…" : "Start with your CV"}</h2>
        <p className="text-sm text-muted-foreground mt-1">Confirm your profile once to unlock matches, fit scores and one-tap applications.</p>
        <button onClick={() => nav("/candidate/profile")} className="mt-4 h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center gap-1.5 hover:brightness-110" data-testid="cv-first-cta">
          Go to profile <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
}
